import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  BarChart3, TrendingUp, Users, Coins, DollarSign, 
  Activity, ArrowUp, ArrowDown, Eye, Crown, Zap,
  Calendar, Clock, ChevronDown, RefreshCw
} from 'lucide-react';

import { BACKEND_URL } from '../config/api';

// Simple bar chart component (no external dependencies)
const SimpleBarChart = ({ data, labels, color = 'orange', height = 120 }) => {
  const max = Math.max(...data, 1);
  
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.map((value, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className={`w-full bg-${color}-500 rounded-t transition-all hover:bg-${color}-600`}
            style={{ 
              height: `${(value / max) * 100}%`,
              minHeight: value > 0 ? 4 : 0,
              backgroundColor: color === 'orange' ? '#f97316' : color === 'emerald' ? '#10b981' : '#6366f1'
            }}
            title={`${labels?.[i] || i}: ${value}`}
          />
          <span className="text-[10px] text-slate-400 truncate w-full text-center">
            {labels?.[i]?.slice(-2) || i}
          </span>
        </div>
      ))}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, trend, color = 'slate' }) => {
  const colorClasses = {
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
  };
  
  return (
    <div className={`p-4 rounded-xl border ${colorClasses[color] || 'bg-slate-50 border-slate-200'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-medium uppercase tracking-wide opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      {subValue && <div className="text-xs text-slate-500 mt-1">{subValue}</div>}
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-xs mt-2 ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          {trend >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
  );
};

// Action Row Component
const ActionRow = ({ action, onClick }) => {
  return (
    <div 
      className="flex items-center justify-between py-3 px-4 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100 last:border-0"
      onClick={() => onClick?.(action.action)}
    >
      <div className="flex-1">
        <div className="font-medium text-slate-800">{action.label}</div>
        <div className="text-xs text-slate-500">{action.unique_users} unique users</div>
      </div>
      <div className="text-right">
        <div className="font-semibold text-slate-900">{action.count}</div>
        <div className="text-xs text-slate-500">uses</div>
      </div>
      <div className="text-right ml-6 min-w-[80px]">
        <div className="font-semibold text-emerald-600">£{action.estimated_revenue.toFixed(2)}</div>
        <div className="text-xs text-slate-500">{action.credits_used} credits</div>
      </div>
    </div>
  );
};

// Main Analytics Dashboard Component
export default function CreditAnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [selectedAction, setSelectedAction] = useState(null);
  const [actionDetails, setActionDetails] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    fetchTrends();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/credits/admin/analytics?days=${days}`,
        { withCredentials: true }
      );
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/credits/admin/analytics/trends`,
        { withCredentials: true }
      );
      setTrends(response.data);
    } catch (error) {
      console.error('Failed to load trends');
    }
  };

  const fetchActionDetails = async (action) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/credits/admin/analytics/action/${action}?days=${days}`,
        { withCredentials: true }
      );
      setActionDetails(response.data);
      setSelectedAction(action);
    } catch (error) {
      toast.error('Failed to load action details');
    }
  };

  if (loading && !analytics) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3" />
          <div className="grid grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-24 bg-slate-100 rounded-xl" />)}
          </div>
          <div className="h-64 bg-slate-100 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="credit-analytics-dashboard">
      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-orange-500" />
              Credit Usage Analytics
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {analytics?.period?.start_date} to {analytics?.period?.end_date}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={fetchAnalytics}
              disabled={loading}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <RefreshCw className={`h-5 w-5 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Activity}
          label="Total Actions"
          value={analytics?.summary?.total_actions || 0}
          color="orange"
        />
        <StatCard
          icon={Coins}
          label="Credits Used"
          value={analytics?.summary?.total_credits_used || 0}
          subValue={`${analytics?.summary?.total_credits_issued || 0} issued`}
          color="purple"
        />
        <StatCard
          icon={DollarSign}
          label="Est. Revenue"
          value={`£${analytics?.summary?.estimated_revenue?.toFixed(2) || '0.00'}`}
          subValue={`£${analytics?.summary?.avg_credit_value?.toFixed(3) || '0'}/credit`}
          color="emerald"
        />
        <StatCard
          icon={Users}
          label="Active Employers"
          value={analytics?.summary?.active_employers || 0}
          subValue={`of ${analytics?.summary?.total_employers || 0} total`}
          color="blue"
        />
      </div>

      {/* Conversion Rates */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Eye className="h-5 w-5 text-blue-500" />
            <span className="font-medium text-slate-700">Profile Conversion</span>
          </div>
          <div className="text-3xl font-bold text-blue-600">
            {analytics?.conversion_rates?.profile_view_to_unlock || 0}%
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {analytics?.conversion_rates?.total_profile_unlocks || 0} unlocks from {analytics?.conversion_rates?.total_profile_views || 0} views
          </div>
          <div className="mt-3 bg-slate-100 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all"
              style={{ width: `${Math.min(analytics?.conversion_rates?.profile_view_to_unlock || 0, 100)}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-5 w-5 text-emerald-500" />
            <span className="font-medium text-slate-700">Employer Activation</span>
          </div>
          <div className="text-3xl font-bold text-emerald-600">
            {analytics?.conversion_rates?.employer_activation || 0}%
          </div>
          <div className="text-sm text-slate-500 mt-1">
            Employers using credits
          </div>
          <div className="mt-3 bg-slate-100 rounded-full h-2">
            <div 
              className="bg-emerald-500 h-2 rounded-full transition-all"
              style={{ width: `${Math.min(analytics?.conversion_rates?.employer_activation || 0, 100)}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Crown className="h-5 w-5 text-amber-500" />
            <span className="font-medium text-slate-700">Founder Impact</span>
          </div>
          <div className="text-3xl font-bold text-amber-600">
            {analytics?.founding_employer_impact?.actions_with_bonus || 0}
          </div>
          <div className="text-sm text-slate-500 mt-1">
            Actions with bonus ({analytics?.founding_employer_impact?.total_savings_from_bonus || 0} credits saved)
          </div>
        </div>
      </div>

      {/* Hiring Funnel Visualization */}
      {analytics?.hiring_funnel && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="hiring-funnel">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-orange-500" />
            Hiring Funnel
          </h3>
          
          {/* Funnel Stages */}
          <div className="space-y-3">
            {analytics.hiring_funnel.stages.map((stage, i) => {
              const maxCount = Math.max(...analytics.hiring_funnel.stages.map(s => s.count), 1);
              const widthPercent = (stage.count / maxCount) * 100;
              const colors = ['bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-emerald-500'];
              
              return (
                <div key={stage.name} className="relative">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">{stage.name}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-slate-900">{stage.count.toLocaleString()}</span>
                      {stage.conversion_to_next !== null && (
                        <span className="text-xs text-slate-500">
                          → {stage.conversion_to_next}% convert
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="h-8 bg-slate-100 rounded-lg overflow-hidden">
                    <div 
                      className={`h-full ${colors[i]} transition-all duration-500 rounded-lg flex items-center justify-end pr-2`}
                      style={{ width: `${Math.max(widthPercent, 5)}%` }}
                    >
                      {widthPercent > 20 && (
                        <span className="text-xs text-white font-medium">{Math.round(widthPercent)}%</span>
                      )}
                    </div>
                  </div>
                  {/* Drop-off indicator */}
                  {i < analytics.hiring_funnel.stages.length - 1 && analytics.hiring_funnel.dropoff_rates && (
                    <div className="flex items-center justify-center my-1">
                      <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded">
                        {Object.values(analytics.hiring_funnel.dropoff_rates)[i]}% drop-off
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-4 border-t border-slate-100">
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">{analytics.hiring_funnel.total_hires}</div>
              <div className="text-xs text-slate-500">Total Hires</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{analytics.hiring_funnel.hiring_employers}</div>
              <div className="text-xs text-slate-500">Hiring Employers</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{analytics?.summary?.hires_per_employer || 0}</div>
              <div className="text-xs text-slate-500">Hires/Employer</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{analytics?.summary?.credits_per_employer || 0}</div>
              <div className="text-xs text-slate-500">Credits/Employer</div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Action Breakdown */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-orange-500" />
              Revenue per Action
            </h3>
            <p className="text-sm text-slate-500">Click an action for detailed breakdown</p>
          </div>
          <div className="divide-y divide-slate-100">
            {analytics?.action_breakdown?.map((action) => (
              <ActionRow 
                key={action.action} 
                action={action} 
                onClick={fetchActionDetails}
              />
            ))}
            {(!analytics?.action_breakdown || analytics.action_breakdown.length === 0) && (
              <div className="p-8 text-center text-slate-500">
                No action data available for this period
              </div>
            )}
          </div>
        </div>

        {/* Top Users */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-500" />
              Top Credit Users
            </h3>
          </div>
          <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
            {analytics?.top_users?.map((user, i) => (
              <div key={user.user_id} className="flex items-center gap-3 p-3">
                <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-sm font-bold text-slate-600">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate flex items-center gap-1">
                    {user.name}
                    {user.founding_employer && (
                      <Crown className="h-3 w-3 text-amber-500" />
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{user.total_actions} actions</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-slate-900">{user.credits_used}</div>
                  <div className="text-xs text-slate-500">credits</div>
                </div>
              </div>
            ))}
            {(!analytics?.top_users || analytics.top_users.length === 0) && (
              <div className="p-8 text-center text-slate-500">
                No user data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Daily Trends Chart */}
      {analytics?.daily_trends?.dates?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            Daily Usage Trend
          </h3>
          <div className="h-32">
            <SimpleBarChart
              data={analytics.daily_trends.credits_used}
              labels={analytics.daily_trends.dates}
              color="orange"
              height={120}
            />
          </div>
        </div>
      )}

      {/* Weekly Trends */}
      {trends?.weekly_trends && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Activity className="h-5 w-5 text-emerald-500" />
              Weekly Actions (12 weeks)
            </h3>
            <div className="h-32">
              <SimpleBarChart
                data={trends.weekly_trends.map(w => w.actions)}
                labels={trends.labels}
                color="emerald"
                height={120}
              />
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-500" />
              New Employers (12 weeks)
            </h3>
            <div className="h-32">
              <SimpleBarChart
                data={trends.new_employers_weekly || []}
                labels={trends.labels}
                color="indigo"
                height={120}
              />
            </div>
          </div>
        </div>
      )}

      {/* Action Details Modal/Panel */}
      {selectedAction && actionDetails && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Zap className="h-5 w-5 text-orange-500" />
              {actionDetails.label} - Detailed Analysis
            </h3>
            <button
              onClick={() => { setSelectedAction(null); setActionDetails(null); }}
              className="text-slate-400 hover:text-slate-600"
            >
              ✕
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-slate-900">{actionDetails.total_uses}</div>
              <div className="text-xs text-slate-500">Total Uses</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-slate-900">{actionDetails.unique_users}</div>
              <div className="text-xs text-slate-500">Unique Users</div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-emerald-600">£{actionDetails.estimated_revenue}</div>
              <div className="text-xs text-slate-500">Est. Revenue</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-orange-600">{actionDetails.current_cost}</div>
              <div className="text-xs text-slate-500">Current Cost</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                <Clock className="h-4 w-4" /> Peak Hour
              </h4>
              <div className="text-lg font-semibold text-slate-900">
                {actionDetails.peak_hour}:00
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Avg Uses/User</h4>
              <div className="text-lg font-semibold text-slate-900">
                {actionDetails.avg_uses_per_user}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
